import {assert} from 'Asserts';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import * as ProcessSchemaDAO from 'processes/dao/ProcessSchemaDAO';
import {ClientSchemaField} from 'registry/field/FieldTable';
import {massageFieldsForSubmit} from 'schemas/SchemaUtil';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';

import {EntryTemplateForConfigureButton, isEntryTemplatePreCreate} from './ConfigureExecutionTemplateButton';
import {isEmptyOrWhitespace} from './ProcessTaskSchemaUtils';

export interface CreateSchemaFormValues {
  processTaskSchemaName: string;
  processTaskSchemaSqlIdentifier?: string;
  taskGroupPrefix: string;
  taskSchemaPrefix: string;
  executionType: ProcessExecutionType;
  canSetAssigneeOnTaskCreation: boolean;
  taskFields: Array<ClientSchemaField>;
  organizationId: string;
  defaultResponsibleParties: Array<MinimalEntity>;
  statusLifecycleId: string;
  defaultCreationFolderId: string | null;
  taskGroupInstructions?: string | null;
  isPropagateWatchersEnabled?: boolean;
  hasOutputSchema: boolean;
  outputSchemaPrefix: string;
  outputFields: Array<ClientSchemaField>;
  executionTemplate: EntryTemplateForConfigureButton;
  defaultEntryExecutionFolderId: string | null;
  canOnlyBePartOfFlowchart: boolean;
  autopopulateDirectOutputsFromTasks: boolean;
}

export function getValidationErrorMessage(values: CreateSchemaFormValues): string | null {
  if (
    isEmptyOrWhitespace(values.taskGroupPrefix) ||
    isEmptyOrWhitespace(values.processTaskSchemaName) ||
    isEmptyOrWhitespace(values.taskSchemaPrefix)
  ) {
    return 'Enter prefixes and a schema name before creating this schema.';
  }
  if (values.taskFields.length === 0) {
    return 'Enter Task fields before creating this schema.';
  }
  if (values.hasOutputSchema) {
    if (isEmptyOrWhitespace(values.outputSchemaPrefix)) {
      return 'Enter an Output prefix before creating this schema.';
    }
    if (values.outputFields.length === 0) {
      return 'This schema has an Output schema. Enter Output fields before creating';
    }
  }
  if (values.executionType === ProcessExecutionType.ENTRY && values.executionTemplate == null) {
    return 'Enter an Execution template before creating this schema.';
  }
  return null;
}

export function buildCreateRequestData(
  values: CreateSchemaFormValues
): ProcessSchemaDAO.CreateProcessTaskSchemaRequest {
  const baseRequestData = {
    name: values.processTaskSchemaName,
    sqlIdentifier: values.processTaskSchemaSqlIdentifier,
    taskGroupPrefix: values.taskGroupPrefix,
    prefix: values.taskSchemaPrefix,
    executionType: values.executionType,
    clientOrigin: ProcessClientOrigin.PROCESSES,
    canSetAssigneeOnTaskCreation: values.canSetAssigneeOnTaskCreation,
    schemaFields: massageFieldsForSubmit(values.taskFields),
    organizationId: values.organizationId,
    defaultResponsiblePartiesIds: values.defaultResponsibleParties.map(({id}) => id),
    statusLifecycleId: values.statusLifecycleId,
    defaultCreationFolderId: values.defaultCreationFolderId,
    taskGroupInstructions: values.taskGroupInstructions,
    isPropagateWatchersEnabled: values.isPropagateWatchersEnabled,
    processOutputSchema: values.hasOutputSchema
      ? {
          prefix: values.outputSchemaPrefix,
          schemaFields: massageFieldsForSubmit(values.outputFields),
        }
      : null,
  };

  if (values.executionType === ProcessExecutionType.ENTRY) {
    assert(
      values.executionTemplate != null && isEntryTemplatePreCreate(values.executionTemplate),
      'Expected execution template to be configured for entry type schema'
    );
    return {
      ...baseRequestData,
      executionType: values.executionType,
      executionTemplate: {
        templateCollectionId: values.executionTemplate.templateCollectionId,
        templateName: values.executionTemplate.name,
        sourceTemplateId: values.executionTemplate.sourceTemplateId,
        publishOnSchemaCreation: values.executionTemplate.publishOnSchemaCreation,
      },
      defaultEntryExecutionFolderId: values.defaultEntryExecutionFolderId,
      canOnlyBePartOfFlowchart: values.canOnlyBePartOfFlowchart,
    };
  } else if (values.executionType === ProcessExecutionType.DIRECT) {
    const requestData: ProcessSchemaDAO.CreateProcessTaskSchemaRequest = {
      ...baseRequestData,
      executionType: values.executionType,
      canOnlyBePartOfFlowchart: values.canOnlyBePartOfFlowchart,
    };
    if (values.hasOutputSchema) {
      requestData.processOutputSchema!.autopopulateFromTasks = values.autopopulateDirectOutputsFromTasks;
    }
    return requestData;
  } else if (values.executionType === ProcessExecutionType.FLOWCHART) {
    return {...baseRequestData, executionType: values.executionType};
  }
  assert(false, `Unsupported execution type - ${values.executionType}`);
}
